import React, { useState, useEffect, useRef } from 'react';
import { Spin, Button, Row, Col } from 'antd';
import moment from 'moment';
import intl from 'react-intl-universal';
import usePortalDrawer, { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import ContentBlock, { FormItem } from '@/components/Reservation/ContentBlock';
import { fetchCrsOrderDetail } from '@/api/channelOrder';
import GuestDetailDrawer, { IGuestDetailRef } from '@/pages/guest/component/GuestDetailDrawer';
import MemberCancelCheckButton from '@/pages/guest/component/MemberCancelCheckButton';
import { DATE_TIME_FORMAT } from '@/config';
import { isNil, parseFen2Currency, parseYuan2Currency } from '@/utils/number';
import { getTimeByUtcOffset } from '@/utils/intl';
import styles from './index.module.scss';

interface IConfig {
  orderNo: String;
}

interface IProps extends IConfig {
  unmountNode: () => void;
}

const DrawerDetail: React.FC<IConfig> = ($$: IProps) => {
  const [modalState, setModalState] = useState({ isLoading: false });
  const [orderDetail, setOrderDetail] = useState<any>({});
  const guestDetailRef = useRef<IGuestDetailRef>();

  const getDetail = async (params) => {
    setModalState({
      isLoading: true,
    });
    try {
      const res = await fetchCrsOrderDetail(params);
      setOrderDetail(res.data);
      setModalState({
        isLoading: false,
      });
    } catch (error) {
      setModalState({
        isLoading: false,
      });
      //
    }
  };

  useEffect(() => {
    getDetail({ orderNo: $$.orderNo });
  }, [$$.orderNo]);

  const getCouponAmount = (item) => {
    if (item) {
      return parseFen2Currency(-item);
    }
    if (item === 0) {
      return parseYuan2Currency(item);
    }
    return '-';
  };

  const getCrsCouponAmount = (item) => {
    if (item) {
      return parseFen2Currency(item);
    }
    if (item === 0) {
      return parseYuan2Currency(item);
    }
    return '-';
  };

  const getPrice = (item) => {
    if (item) {
      return parseFen2Currency(item);
    }
    if (item === 0) {
      return parseYuan2Currency(item);
    }
    return item;
  };

  const getCustNo = (custNo) => {
    if (custNo) {
      const reg = /^(.{4})(?:\d+)(.{4})$/;
      const newCustNo = custNo.replace(reg, '$1**********$2');
      return newCustNo;
    }
    return '';
  };

  const handleOpenGuestDetail = (record: any) => {
    guestDetailRef.current.openDrawer({ guestRecord: record });
  };

  return (
    <Spin spinning={modalState.isLoading}>
      <DrawerHeader title={intl.get('channel_crs_channel_order_detail')} onClose={$$.unmountNode} />
      <GuestDetailDrawer ref={guestDetailRef} />
      <section className={styles.container}>
        <div className={styles.contentBox}>
          <ContentBlock title={intl.get('channel_order_info')}>
            <div className={styles.contentInfo}>
              <Row gutter={[0, 24]}>
                <Col span={8}>
                  <div className={styles.baseInfoItem}>
                    <label>{intl.get('channel_make_order_client_colon')}</label>
                    <span>{orderDetail?.orderInfoDto?.orderSource}</span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className={styles.baseInfoItem}>
                    <label>{intl.get('channel_order_number_colon')}</label>
                    <span>{orderDetail?.orderInfoDto?.orderNo}</span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className={styles.baseInfoItem}>
                    <label>{intl.get('channel_make_order_time_colon')}</label>
                    <span>
                      {getTimeByUtcOffset(orderDetail?.orderInfoDto?.orderTime, DATE_TIME_FORMAT)}
                    </span>
                  </div>
                </Col>
              </Row>
              <Row gutter={[0, 24]}>
                <Col span={8}>
                  <div className={styles.baseInfoItem}>
                    <label>{intl.get('channel_order_status_colon')}</label>
                    <span>{orderDetail?.orderInfoDto?.orderStatus}</span>
                  </div>
                </Col>
              </Row>
              <div className={styles.baseModule}>
                <Row gutter={[0, 24]}>
                  <Col span={8}>
                    <div className={styles.baseInfoItem}>
                      <label>{intl.get('channel_book_room_type_colon')}</label>
                      <span>{orderDetail?.orderInfoDto?.roomTypeName}</span>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className={styles.baseInfoItem}>
                      <label>{intl.get('channel_book_room_num_colon')}</label>
                      <span>{orderDetail?.orderInfoDto?.roomNum}</span>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className={styles.baseInfoItem}>
                      <label>{intl.get('channel_room_type_colon')}</label>
                      <span>{orderDetail?.orderInfoDto?.roomType}</span>
                    </div>
                  </Col>
                </Row>
                <Row gutter={[0, 24]}>
                  <Col span={8}>
                    <div className={styles.baseInfoItem}>
                      <label>{intl.get('channel_book_date_colon')}</label>
                      <span>{orderDetail?.orderInfoDto?.checkInTime}</span>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className={styles.baseInfoItem}>
                      <label>{intl.get('channel_live_duration_colon')}</label>
                      <span>{orderDetail?.orderInfoDto?.nights}</span>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className={styles.baseInfoItem}>
                      <label>{intl.get('channel_book_arrival_time_colon')}</label>
                      <span>{orderDetail?.orderInfoDto?.arriveTime}</span>
                    </div>
                  </Col>
                </Row>
                <Row gutter={[0, 24]}>
                  <Col span={8}>
                    <div className={styles.baseInfoItem}>
                      <label>{intl.get('channel_book_name_colon')}</label>
                      <span>{orderDetail?.orderInfoDto?.userName}</span>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className={styles.baseInfoItem}>
                      <label>{intl.get('channel_mobile_colon')}</label>
                      <span>{orderDetail?.orderInfoDto?.userPhone}</span>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className={styles.baseInfoItem}>
                      <label>{intl.get('channel_is_vip_colon')}</label>
                      <span>{orderDetail?.orderInfoDto?.isMember}</span>
                    </div>
                  </Col>
                </Row>
                <Row gutter={[0, 24]}>
                  <Col span={8}>
                    <div className={styles.baseInfoItem}>
                      <label>{intl.get('channel_book_product_colon')}</label>
                      <span>{orderDetail?.orderInfoDto?.productName}</span>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className={styles.baseInfoItem}>
                      <label>{intl.get('channel_order_member_info_colon')}</label>
                      <div>
                        {getCustNo(orderDetail?.orderInfoDto?.custNo)}
                        {orderDetail?.orderInfoDto?.custNo ? (
                          <MemberCancelCheckButton
                            type="default"
                            text={intl.get('app_look')}
                            wyuid={orderDetail?.orderInfoDto?.wyuid}
                            businessDate={orderDetail?.orderInfoDto?.orderTime}
                            style={{ marginLeft: '10px' }}
                            onClick={() => {
                              handleOpenGuestDetail({ ...orderDetail?.orderInfoDto });
                            }}
                          />
                        ) : (
                          orderDetail?.orderInfoDto?.userPhone
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
              <div className={styles.baseModule}>
                <Row gutter={[0, 24]}>
                  <Col span={16}>
                    <div className={styles.baseInfoItem}>
                      <label>{intl.get('channel_book_remark_colon')}</label>
                      <span className={styles.readStyle}>
                        {orderDetail?.orderInfoDto?.userRemark}
                      </span>
                    </div>
                  </Col>
                </Row>
                <Row gutter={[0, 24]}>
                  <Col span={16}>
                    <div className={styles.baseInfoItem}>
                      <label>{intl.get('channel_book_read_colon')}</label>
                      <div>
                        {orderDetail?.orderInfoDto?.orderMustRead &&
                          orderDetail?.orderInfoDto?.orderMustRead.map((item, index) => {
                            return (
                              <p key={index.toString()} className={styles.readStyle}>
                                {item}
                              </p>
                            );
                          })}
                      </div>
                    </div>
                  </Col>
                </Row>
                <div className={styles.ruleBox}>
                  <Row gutter={[0, 24]}>
                    <Col span={24}>
                      <div className={styles.baseInfoItem}>
                        <label>{intl.get('channel_cancel_rule_colon')}</label>
                        <p className={styles.readStyle}>{orderDetail?.orderInfoDto?.cancelRule}</p>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </ContentBlock>
          <ContentBlock title={intl.get('channel_room_fee_info')}>
            <div className={styles.contentInfo}>
              <Row gutter={[0, 24]}>
                <Col span={8}>
                  <div className={styles.baseInfoItem}>
                    <label>{intl.get('channel_should_pay_money')}</label>
                    <span>{parseFen2Currency(orderDetail?.roomPriceInfoDto?.orderAmount)}</span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className={styles.baseInfoItem}>
                    <label>{intl.get('channel_user_pay_colon')}</label>
                    <span>{parseFen2Currency(orderDetail?.roomPriceInfoDto?.payAmount)}</span>
                  </div>
                </Col>
                <Col>
                  <div className={styles.baseInfoItem}>
                    <label>{intl.get('channel_hotel_points_deduction')}</label>
                    <span>
                      {parseFen2Currency(orderDetail?.roomPriceInfoDto?.pointsDeductionAmount)}
                      {orderDetail?.roomPriceInfoDto?.points
                        ? `（${orderDetail?.roomPriceInfoDto?.points ?? ''}${intl.get(
                            'channel_hotel_points_deduction',
                          )}）`
                        : ''}
                    </span>
                  </div>
                </Col>
              </Row>
              <Row gutter={[0, 24]}>
                <Col span={8}>
                  <div className={styles.baseInfoItem}>
                    <label>{intl.get('channel_pay_channel_colon')}</label>
                    <span>{orderDetail?.roomPriceInfoDto?.payWay}</span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className={styles.baseInfoItem}>
                    <label>{intl.get('channel_pay_no_code_colon')}</label>
                    <span>{orderDetail?.roomPriceInfoDto?.payNo}</span>
                  </div>
                </Col>
                <Col span={8}>
                  <div className={styles.baseInfoItem}>
                    <label>{intl.get('channel_pay_time_colon')}</label>
                    <span>
                      {orderDetail?.roomPriceInfoDto?.payTime
                        ? getTimeByUtcOffset(
                            orderDetail?.roomPriceInfoDto?.payTime,
                            DATE_TIME_FORMAT,
                          )
                        : null}
                    </span>
                  </div>
                </Col>
              </Row>
              <Row gutter={[0, 24]}>
                <Col span={8}>
                  <div className={styles.baseInfoItem}>
                    <label>{intl.get('channel_price_strategy_colon')}</label>
                    <span>{orderDetail?.roomPriceInfoDto?.priceStrategyName}</span>
                  </div>
                </Col>
              </Row>
              <div className={styles.tables}>
                <div className={styles.tabHeader}>
                  <span className={styles.headSpan}>{intl.get('app_date')}</span>
                  <span className={styles.headSpan}>{intl.get('app_room')}</span>
                  <span className={styles.headSpan}>{intl.get('channel_room_price')}</span>
                  <span className={styles.headSpan}>{intl.get('channel_direct_coupon')}</span>
                  <span className={styles.headSpan}>{intl.get('channel_promotion_type')}</span>
                  <span className={styles.headSpan}>
                    {intl.get('channel_store_promotion_amount')}
                  </span>
                  {/* 新增限时特惠 */}
                  <span className={styles.headSpan}>
                    {intl.get('channel_store_one_mounth_price')}
                  </span>
                  <span className={styles.headSpan}>{intl.get('channel_crs_use_fee')}</span>
                  <span className={styles.headSpan}>{intl.get('channel_execute_room_price')}</span>
                </div>

                {orderDetail?.roomPriceInfoDto?.roomPriceDetailDtos ? (
                  orderDetail?.roomPriceInfoDto?.roomPriceDetailDtos.map((item, index) => {
                    return (
                      <div className={styles.tabBody} key={index.toString()}>
                        <span className={styles.bodySpan}>
                          {getTimeByUtcOffset(item.time, DATE_TIME_FORMAT)}
                        </span>
                        <span className={styles.bodySpan}>
                          {item.roomName || item.roomName === 0 ? `${item.roomName}` : '-'}
                        </span>
                        <span className={styles.bodySpan}>
                          {item.roomAmount || item.roomAmount === 0
                            ? parseFen2Currency(item.roomAmount)
                            : '-'}
                        </span>
                        <span className={styles.bodySpan}>
                          {getCouponAmount(item.couponAmount)}
                        </span>
                        <span className={styles.bodySpan}>{item.crsPromotionName}</span>
                        <span className={styles.bodySpan}>
                          {getCouponAmount(item.crsPromotionDiscount)}
                        </span>
                        <span className={styles.bodySpan}>
                          {getCouponAmount(item.fixedPriceDiscountAmount)}
                        </span>
                        <span className={styles.bodySpan}>
                          {item.crsUseAmount || item.crsUseAmount === 0
                            ? parseFen2Currency(item.crsUseAmount)
                            : '-'}
                        </span>
                        <span className={styles.bodySpan}>
                          {item.executeAmount || item.executeAmount === 0
                            ? parseFen2Currency(item.executeAmount)
                            : '-'}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.tabBodyNo}>{intl.get('app_no_data')}</div>
                )}
                <div className={styles.tabFoot}>
                  <span className={styles.footSpan}>
                    {intl.get('drawer_crs_reserve_room_summary')}
                  </span>
                  <span className={styles.footSpan}>
                    {orderDetail?.roomPriceInfoDto?.totalRoomNum}
                  </span>
                  <span className={styles.footSpan}>
                    {getCrsCouponAmount(orderDetail?.roomPriceInfoDto?.totalRoomAmount)}
                  </span>
                  <span className={styles.footSpan}>
                    {getCouponAmount(orderDetail?.roomPriceInfoDto?.totalCouponAmount)}
                  </span>
                  <span className={styles.footSpan} />
                  <span className={styles.footSpan} />
                  <span className={styles.footSpan}>
                    {getCouponAmount(orderDetail?.roomPriceInfoDt?.totalFixedPriceDiscountAmout)}
                  </span>
                  <span className={styles.footSpan}>
                    {getCrsCouponAmount(orderDetail?.roomPriceInfoDto?.totalCrsUseAmount)}
                  </span>
                  <span className={styles.footSpan}>
                    {getCrsCouponAmount(orderDetail?.roomPriceInfoDto?.totalExecuteAmount)}
                  </span>
                </div>
              </div>
            </div>
          </ContentBlock>
          <ContentBlock title={intl.get('bill_cross_refound_infos')}>
            <div className={styles.tablesWrapper}>
              <div className={styles.tabHeader}>
                <span className={styles.headSpan}>{intl.get('channel_refund_date')}</span>
                <span className={styles.headSpan}>{intl.get('channel_refund_pms_room_price')}</span>
                <span className={styles.headSpan}>{intl.get('channel_refund_crs_use_fee')}</span>
                <span className={styles.headSpan}>{intl.get('channel_refund_direct_coupon')}</span>
                <span className={styles.headSpan}>{intl.get('channel_user_actual_amount')}</span>
                <span className={styles.headSpan} style={{ width: 400 }}>
                  {intl.get('channel_refund_hotel_points')}
                </span>
              </div>

              {orderDetail?.orderRefundInfoDto?.refundDetailDtos ? (
                orderDetail?.orderRefundInfoDto?.refundDetailDtos.map((item, index) => {
                  return (
                    <div className={styles.tabBody} key={index.toString()}>
                      <span className={styles.bodySpan}>
                        {getTimeByUtcOffset(item.refundTime, DATE_TIME_FORMAT)}
                      </span>
                      <span className={styles.bodySpan}>
                        {item.pmsRoomAmount || item.pmsRoomAmount === 0
                          ? parseFen2Currency(item.pmsRoomAmount)
                          : '-'}
                      </span>
                      <span className={styles.bodySpan}>
                        {item.crsUseAmount || item.crsUseAmount === 0
                          ? parseFen2Currency(item.crsUseAmount)
                          : '-'}
                      </span>
                      <span className={styles.bodySpan}>
                        {item.couponAmount || item.couponAmount === 0
                          ? parseFen2Currency(item.couponAmount)
                          : '-'}
                      </span>
                      <span className={styles.bodySpan}>
                        {item.refundRealUserAmount || item.refundRealUserAmount === 0
                          ? parseFen2Currency(item.refundRealUserAmount)
                          : '-'}
                      </span>
                      <span className={styles.bodySpan} style={{ width: 400 }}>
                        {item.refundPoints || item.refundPoints === 0
                          ? `${item.refundPoints}${intl.get('channel_points')}（${intl.get(
                              'channel_worth',
                            )}${parseFen2Currency(item.pointsDeductionAmount)}）`
                          : '-'}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className={styles.tabBodyNo}>{intl.get('app_no_data')}</div>
              )}
              <div className={styles.tabFoot}>
                <span className={styles.footSpan}>
                  {intl.get('drawer_crs_reserve_room_summary')}
                </span>
                <span className={styles.footSpan}>
                  {getPrice(orderDetail?.orderRefundInfoDto?.totalPmsRoomAmount)}
                </span>
                <span className={styles.footSpan}>
                  {getPrice(orderDetail?.orderRefundInfoDto?.totalCrsUseAmount)}
                </span>
                <span className={styles.footSpan}>
                  {getPrice(orderDetail?.orderRefundInfoDto?.totalCouponAmount)}
                </span>
                <span className={styles.footSpan}>
                  {getPrice(orderDetail?.orderRefundInfoDto?.totalRefundRealUserAmount)}
                </span>
                <span className={styles.footSpan} style={{ width: 400 }}>
                  {!isNil(orderDetail?.orderRefundInfoDto?.totalRefundPoints)
                    ? `${orderDetail?.orderRefundInfoDto?.totalRefundPoints}${intl.get(
                        'channel_points',
                      )}（${intl.get('channel_worth')}${parseFen2Currency(
                        orderDetail?.orderRefundInfoDto?.totalPointsDeductionAmount,
                      )}）`
                    : '-'}
                </span>
              </div>
            </div>
          </ContentBlock>
          {orderDetail?.orderInfoDto?.closeSourceDesc ? (
            <ContentBlock title={intl.get('bill_cross_cancel_infos')}>
              <div className={styles.contentInfo}>
                <Row gutter={[0, 24]}>
                  <Col span={8}>
                    <div className={styles.baseInfoItem}>
                      <label>{intl.get('bill_cross_cancel_type')}:</label>
                      <span>{orderDetail?.orderInfoDto?.closeSourceDesc}</span>
                    </div>
                  </Col>
                </Row>
              </div>
            </ContentBlock>
          ) : null}
        </div>
      </section>
      <DrawerFooter>
        <Button onClick={$$.unmountNode}>{intl.get('app_confirm_close_btn')}</Button>
      </DrawerFooter>
    </Spin>
  );
};

export default () => usePortalDrawer<IConfig>(DrawerDetail);
